"use client"

import Image from "next/image"
import { Badge } from "@/components/ui/badge"
import { useState } from "react"

interface Speaker {
  name: string
  role: string
  organization?: string
  bio: string
  image: string
  type: "speaker" | "judge" | "moderator" | "panelist"
}

const speakers: Speaker[] = [
  {
    name: "Bojuwoye Emmanuel",
    role: "Growth Marketer & Automation Specialist",
    organization: "Locus Africa",
    image: "/disruptED26/speakers/bojuwoye-emmanuel.jpeg",
    type: "speaker",
    bio: "Tech-focused growth marketer and automation specialist. Lead at Locus Africa, expanding access to practical, future-ready digital skills across Africa. Passionate about how no-code tools, automation, and AI are changing the way people work, learn, and create value.",
  },
  {
    name: "Oladotun Ajayi",
    role: "Co-founder & Social Entrepreneur",
    organization: "Evolv Africa",
    image: "/disruptED26/speakers/oladotun-ajayi.jpeg",
    type: "speaker",
    bio: "Social entrepreneur and public health professional focused on advancing human capital development and inclusive access to opportunity. Co-founder of Evolv Africa, connecting young talent with employers and global opportunities.",
  },
  {
    name: "Oluwatobi Oseni",
    role: "CEO & Co-Founder",
    organization: "Payshiga Technologies",
    image: "/disruptED26/speakers/oluwatobi-oseni.jpeg",
    type: "judge",
    bio: "Fintech innovator leading Payshiga Technologies. Judge for the Africathon competition, bringing expertise in technology and entrepreneurship to evaluate groundbreaking ventures.",
  },
  {
    name: "Tomide Williams",
    role: "Product Marketing Manager",
    organization: "Koppoh & Drivewell",
    image: "/disruptED26/speakers/tomide-williams.jpeg",
    type: "speaker",
    bio: "Product marketing leader with experience in AI, SaaS, EdTech, and mobility. 2× founder with 45K+ followers, specializing in product positioning, audience psychology, and demand generation.",
  },
  {
    name: "Adetomi Soyinka",
    role: "Program Officer",
    organization: "Mastercard Foundation",
    image: "/disruptED26/speakers/adetomi-soyinka.jpeg",
    type: "panelist",
    bio: "Expert on education and economic opportunity. Panelist in the 'Future of Work' discussion, bringing perspectives on talent development and financial inclusion across Africa.",
  },
  {
    name: "Alex Onyia",
    role: "CEO",
    organization: "Educare",
    image: "/disruptED26/speakers/alex-onyia.jpeg",
    type: "panelist",
    bio: "EdTech leader and innovator. Moderator for the AI and EdTech panel, exploring artificial intelligence applications in education and digital transformation.",
  },
  {
    name: "Modupe Oreye",
    role: "Event Host & Panel Moderator",
    organization: "Evolv Africa (COO)",
    image: "/disruptED26/speakers/modupe-oreye.jpeg",
    type: "moderator",
    bio: "Seasoned compere, public speaker, and social impact advocate. Chief Operating Officer at Evolv Africa. Commands rooms with clarity and ensures speakers deliver their best.",
  },
  {
    name: "Muhammad Abdallah",
    role: "Education & Science Professional",
    organization: "Medmina College",
    image: "/disruptED26/speakers/muhammad-abdallah.jpeg",
    type: "speaker",
    bio: "PhD in Educational Management. Head of Human Resources at Medmina College and Oyo State Secretary of STAN. Dedicated to teacher and student development through professional programs.",
  },
  {
    name: "Prince Lawrence Ezeh",
    role: "Honourable Commissioner",
    organization: "Enugu State Government",
    image: "/disruptED26/speakers/prince-lawrence-ezeh.jpeg",
    type: "speaker",
    bio: "Commissioner for Innovation, Science and Technology, Enugu State Government. Leading innovation and technology initiatives at the state level.",
  },
  {
    name: "Ashley Immanuel",
    role: "Co-founder",
    organization: "Semicolon",
    image: "/placeholder.svg",
    type: "panelist",
    bio: "Technology and education innovator. Panelist in the 'Future of Work' discussion, bringing perspectives on tech education and career development.",
  },
]

const getTypeBadgeColor = (type: Speaker["type"]) => {
  const colors: Record<Speaker["type"], string> = {
    speaker: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    judge: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    moderator: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
    panelist: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  }
  return colors[type]
}

export function DisruptEDSpeakersSection() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const toggleExpanded = (name: string) => {
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }))
  }

  const speakersByType = {
    speaker: speakers.filter((s) => s.type === "speaker"),
    judge: speakers.filter((s) => s.type === "judge"),
    moderator: speakers.filter((s) => s.type === "moderator"),
    panelist: speakers.filter((s) => s.type === "panelist"),
  }

  const renderSpeakerGroup = (title: string, speakerList: Speaker[]) => (
    <div key={title} className="mb-20">
      <h3 className="mb-10 text-3xl font-extrabold text-foreground border-l-4 border-secondary pl-4 tracking-tight">
        {title}
      </h3>
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {speakerList.map((speaker) => (
          <div
            key={speaker.name}
            className={`group overflow-hidden rounded-2xl border transition-all duration-500 flex flex-col ${
              expanded[speaker.name] 
                ? "border-secondary/50 shadow-2xl bg-card scale-[1.02] z-10" 
                : "border-border bg-slate-50/50 dark:bg-slate-900/50 hover:shadow-xl hover:border-secondary/20"
            }`}
          >
            <div className={`relative overflow-hidden bg-slate-200 dark:bg-slate-800 transition-all duration-500 ${
              expanded[speaker.name] ? "aspect-[16/9]" : "aspect-square"
            }`}>
              <Image
                src={speaker.image}
                alt={speaker.name}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-110"
                onError={(e) => {
                  e.currentTarget.src = "/placeholder.svg?height=600&width=600"
                }}
              />
              <div className="absolute top-4 left-4">
                <Badge className={`${getTypeBadgeColor(speaker.type)} border-none shadow-md px-3 py-1 font-bold uppercase tracking-wider text-[10px]`}>
                  {speaker.type}
                </Badge>
              </div>
            </div>
            <div className="p-6 flex-grow flex flex-col">
              <div className="mb-4">
                <h4 className="text-2xl font-bold text-foreground mb-1">
                  {speaker.name}
                </h4>
                <p className="text-secondary font-bold text-sm uppercase tracking-wide">
                  {speaker.role}
                </p>
                {speaker.organization && (
                  <p className="text-muted-foreground text-sm font-medium mt-1 italic">
                    {speaker.organization}
                  </p>
                )}
              </div>

              <div className="relative">
                <p className={`text-muted-foreground leading-relaxed transition-all duration-500 ${
                  expanded[speaker.name] ? "text-base" : "text-sm line-clamp-4"
                }`}>
                  {speaker.bio}
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-border flex justify-between items-center">
                <button
                  onClick={() => toggleExpanded(speaker.name)}
                  className="text-sm font-bold text-secondary hover:text-secondary/80 flex items-center group/btn"
                >
                  {expanded[speaker.name] ? (
                    <>Show less <span className="ml-2 rotate-180 transition-transform block">↓</span></>
                  ) : (
                    <>View full profile <span className="ml-2 group-hover/btn:translate-y-1 transition-transform block">↓</span></>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <section id="speakers-detailed" className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="mx-auto mb-20 max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-secondary text-secondary font-bold px-4 py-1 uppercase tracking-[0.2em]">
            Summit Faculty
          </Badge>
          <h2 className="mb-6 text-4xl font-black text-foreground md:text-5xl lg:text-7xl tracking-tighter">
            Distinguished <span className="text-secondary">Speakers</span>
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed font-medium">
            Join the conversation with the foremost voices in education technology, policy, and social impact across the African continent.
          </p>
        </div>

        <div className="mx-auto">
          {speakersByType.speaker.length > 0 &&
            renderSpeakerGroup("Featured Speakers", speakersByType.speaker)}
          {speakersByType.panelist.length > 0 &&
            renderSpeakerGroup("Panel Members", speakersByType.panelist)}
          {speakersByType.moderator.length > 0 &&
            renderSpeakerGroup("Moderators", speakersByType.moderator)}
          {speakersByType.judge.length > 0 &&
            renderSpeakerGroup("Competition Judges", speakersByType.judge)}
        </div>
      </div>
    </section>
  )
}
